import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from '../StudyManagement.module.css';
import { ENROLLMENT_STATUSES } from '../Contants';
import { convertDateFormat } from '../utils';
import { Calendar } from 'lucide-react';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';


const formatDateDisplay = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const SubjectDrawer = ({
  isOpen,
  onClose,
  mode,
  initialData,
  sites,
  loadingSites,
  userAssignedSiteId,
  onSubmit,
  onCreateAndCapture,
  generateSubjectId,
  checkSiteLimit,
}) => {

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    siteName: '',
    siteId: '',
    subjectId: '',
    subjectInitials: '',
    enrollmentDate: '',
    enrollmentStatus: 'screening',
    userId: null,
    studyId: null,
  });

  const [enrollmentInfo, setEnrollmentInfo] = useState(null);

  const [modal, setModal] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info',
    confirmText: 'OK',
    cancelText: null,
    onConfirm: null,
    onCancel: null
  });

  const showModal = (config) => {
    setModal({
      show: true,
      title: config.title || '',
      message: config.message || '',
      type: config.type || 'info',
      confirmText: config.confirmText || 'OK',
      cancelText: config.cancelText || null,
      onConfirm: config.onConfirm || closeModal,
      onCancel: config.onCancel || closeModal
    });
  };

  const closeModal = () => {
    setModal({
      show: false,
      title: '',
      message: '',
      type: 'info',
      confirmText: 'OK',
      cancelText: null,
      onConfirm: null,
      onCancel: null
    });
  };

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        siteName: '',
        siteId: '',
        subjectId: '',
        subjectInitials: '',
        enrollmentDate: '',
        enrollmentStatus: 'screening',
        userId: null,
        studyId: null,
      });
      setEnrollmentInfo(null);
      return;
    }

    const userDetails = JSON.parse(localStorage.getItem('user')) || {};
    const currentUserId = userDetails.userId;
    const currentStudyId = userDetails.study?.studyId || userDetails.studyId;

    if (mode === 'create' && userAssignedSiteId) {
      const assignedSite = sites.find(
        (s) => s.id === Number(userAssignedSiteId)
      );

      if (assignedSite) {
        setFormData({
          siteName: `${assignedSite.code} - ${assignedSite.name}`,
          siteId: assignedSite.id,
          subjectId: generateSubjectId(assignedSite.code),
          subjectInitials: '',
          enrollmentDate: '',
          enrollmentStatus: initialData?.enrollmentStatus || 'screening',
          userId: currentUserId,
          studyId: currentStudyId,
        });

        if (initialData) {
          setEnrollmentInfo({
            maxSubjects: initialData.maxSubjects,
            enrolledSubjects: initialData.enrolledSubjects,
            availableSlots: initialData.availableSlots,
            progress: Math.round((initialData.enrolledSubjects / initialData.maxSubjects) * 100)
          });
        } else {
          setEnrollmentInfo({
            maxSubjects: assignedSite.maxEnrollment,
            enrolledSubjects: assignedSite.enrolled,
            availableSlots: assignedSite.maxEnrollment - assignedSite.enrolled,
            progress: assignedSite.progress
          });
        }
      }
    } else if ((mode === 'edit' || mode === 'view') && initialData) {
      setFormData({
        siteName: initialData.siteName,
        siteId: initialData.siteId,
        subjectId: initialData.subjectId,
        subjectInitials: initialData.subjectInitials,
        enrollmentDate: convertDateFormat(initialData.dateOfEnrollment),
        enrollmentStatus: initialData.enrollmentStatus.toLowerCase(),
        userId: currentUserId,
        studyId: currentStudyId,
      });

      const selectedSite = sites.find((s) => s.id === Number(initialData.siteId));
      if (selectedSite) {
        setEnrollmentInfo({
          maxSubjects: selectedSite.maxEnrollment,
          enrolledSubjects: selectedSite.enrolled,
          availableSlots: selectedSite.maxEnrollment - selectedSite.enrolled,
          progress: selectedSite.progress
        });
      }
    }
  }, [isOpen, mode, initialData, userAssignedSiteId, sites, generateSubjectId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'siteId') {
      const site = sites.find((s) => s.id === Number(value));
      setFormData((prev) => ({
        ...prev,
        siteId: value,
        siteName: `${site.code} - ${site.name}`,
        subjectId: generateSubjectId(site.code),
      }));
      return;
    }

    if (name === 'subjectInitials') {
      const lettersOnly = value.replace(/[^A-Za-z]/g, '').toUpperCase();
      const limitedLetters = lettersOnly.slice(0, 3);
      setFormData((prev) => ({ ...prev, [name]: limitedLetters }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const requiredFields = [
      'siteId',
      'subjectId',
      'subjectInitials',
      'enrollmentDate',
    ];

    for (let f of requiredFields) {
      if (!formData[f]) {
        showModal({
          title: 'Validation Error',
          message: 'Please fill in all required fields',
          type: 'warning',
          confirmText: 'OK'
        });
        return false;
      }
    }

    if (!formData.userId) {
      showModal({
        title: 'Authentication Error',
        message: 'User ID not found. Please login again.',
        type: 'error',
        confirmText: 'OK'
      });
      return false;
    }

    if (!formData.studyId) {
      showModal({
        title: 'Study Error',
        message: 'Study ID not found. Please select a study.',
        type: 'error',
        confirmText: 'OK'
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const finalFormData = {
      ...formData,
      subjectInitials: formData.subjectInitials.padEnd(3, '-')
    };

    setIsSubmitting(true);

    try {
      const result = await onSubmit(finalFormData);

      if (result && result.success === false) {
        showModal({
          title: 'Error',
          message: result.message || 'Failed to create subject. Please try again.',
          type: 'error',
          confirmText: 'OK'
        });
      } else if (result && result.success) {
        showModal({
          title: 'Success',
          message: result.message || 'Subject created successfully!',
          type: 'success',
          confirmText: 'OK',
          onConfirm: () => {
            closeModal();
            onClose();
          }
        });
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.data?.message ||
        error.message ||
        'Failed to create subject. Please try again.';

      showModal({
        title: 'Error',
        message: errorMessage,
        type: 'error',
        confirmText: 'OK'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateAndCapture = async () => {
    if (!validateForm()) return;

    const finalFormData = {
      ...formData,
      subjectInitials: formData.subjectInitials.padEnd(3, '-')
    };

    setIsSubmitting(true);

    try {
      const result = await onCreateAndCapture(finalFormData);

      if (result && result.success === false) {
        showModal({
          title: 'Error',
          message: result.message || 'Failed to create subject. Please try again.',
          type: 'error',
          confirmText: 'OK'
        });
      } else if (result && result.success) {
        showModal({
          title: 'Success',
          message: result.message || 'Subject created successfully!',
          type: 'success',
          confirmText: 'OK',
          onConfirm: () => {
            closeModal();
            onClose();
          }
        });
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.data?.message ||
        error.message ||
        'Failed to create subject. Please try again.';

      showModal({
        title: 'Error',
        message: errorMessage,
        type: 'error',
        confirmText: 'OK'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedSite = sites.find((s) => s.id === Number(formData.siteId));
  const isFormDisabled = mode === 'view';

  const getDrawerTitle = () => {
    if (mode === 'create') return 'Create New Subject';
    if (mode === 'edit') return 'Edit Subject';
    if (mode === 'view') return 'View Subject';
    return 'Subject Details';
  };

  if (!isOpen) return null;

  return (
    <>
      <ConfirmationModal
        show={modal.show}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={modal.confirmText}
        cancelText={modal.cancelText}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
      />

      <div className={styles.backdrop} onClick={onClose}></div>

      <div className={styles.drawer}>
        <div className={styles.drawerHeader}>
          <h2 className={styles.drawerTitle}>{getDrawerTitle()}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className={styles.drawerContent}>
          <form className={styles.form}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Site Name <span className={styles.required}>*</span>
              </label>

              {loadingSites ? (
                <div className={styles.loadingText}>Loading sites...</div>
              ) : (
                <input
                  className={styles.input}
                  name='siteName'
                  value={formData.siteName}
                  disabled
                  readOnly
                />
              )}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                Subject Initials <span className={styles.required}>*</span>
              </label>
              <input
                className={styles.input}
                name='subjectInitials'
                value={formData.subjectInitials}
                onChange={handleInputChange}
                disabled={isFormDisabled}
                placeholder='E.g., JD'
                maxLength={3}
              />
            </div>

            <div className={styles.formGroup}>
  <label className={styles.label}>
    Enrollment Date <span className={styles.required}>*</span>
  </label>
  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
    <input
      className={styles.input}
      value={formData.enrollmentDate ? formatDateDisplay(formData.enrollmentDate) : ''}
      placeholder='DD-MMM-YYYY'
      disabled={isFormDisabled}
      readOnly
      style={{ 
        cursor: isFormDisabled ? 'not-allowed' : 'pointer',
        paddingRight: '40px',
        width: '100%',
        pointerEvents: 'none'
      }}
    />
    <input
      type='date'
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        opacity: 0,
        cursor: isFormDisabled ? 'not-allowed' : 'pointer',
        zIndex: 2
      }}
      name='enrollmentDate'
      value={formData.enrollmentDate}
      onChange={handleInputChange}
      disabled={isFormDisabled}
      min={new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]}
      max={new Date().toISOString().split('T')[0]}
    />
    <Calendar
      size={18}
      style={{
        position: 'absolute',
        right: '12px',
        pointerEvents: 'none',
        color: '#6b7280',
        zIndex: 1
      }}
    />
  </div>
</div>

            {selectedSite && enrollmentInfo && (
              <div className={styles.formGroup}>
                <label className={styles.label}>Site Enrollment Status</label>
                <div className={styles.statusInfo}>
                  <div>
                    <p className={styles.statusTitle}>
                      {selectedSite.code} - {selectedSite.name}
                    </p>
                    <p className={styles.statusDetail}>
                      {enrollmentInfo.enrolledSubjects} of {enrollmentInfo.maxSubjects}{' '}
                      subjects enrolled
                    </p>
                    <div className={styles.progressBar}>
                      <div
                        className={styles.progressFill}
                        style={{ width: `${enrollmentInfo.progress}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>

        <div className={styles.drawerFooter}>
          {mode === 'view' ? (
            <button className={styles.cancelButton} onClick={onClose}>
              Close
            </button>
          ) : mode === 'edit' ? (
            <>
              <button className={styles.cancelButton} onClick={onClose}>
                Cancel
              </button>
              <button
                className={styles.primaryButton}
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <>
              <button className={styles.cancelButton} onClick={onClose}>
                Cancel
              </button>
              <button
                className={styles.enrollButton}
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Enroll New Subject'}
              </button>
              {/* <button
                className={styles.primaryButton}
                onClick={handleCreateAndCapture}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create & Start Capture'}
              </button> */}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SubjectDrawer;